import { Controller } from "@hotwired/stimulus";

// Chip input for array fields. Each chip carries:
//   - a visible <span> with the value (clickable to edit inline)
//   - a hidden <input name="...[]"> the form submits
//   - a × button to remove
//
// Markup expected:
//   <div data-controller="chip-input"
//        data-chip-input-name-value="junior_configuration[niveau_etude][]"
//        data-chip-input-auto-submit-value="true">
//     <div data-chip-input-target="chips">
//       <!-- existing chips rendered server-side -->
//     </div>
//     <input type="text" data-chip-input-target="entry"
//            data-action="keydown.enter->chip-input#add ..." />
//   </div>
//
// Auto-submit (default true): committing or removing a chip submits the
// closest <form>, so the user never has to click "Enregistrer".
export default class extends Controller {
  static targets = ["chips", "entry"];
  static values = {
    name:       String,
    autoSubmit: { type: Boolean, default: true }
  };

  connect() {
    // Wire up existing chips (rendered server-side) for click-to-edit.
    this.chipsTarget.querySelectorAll(".jc-chip").forEach((chip) => {
      this._bindChipForEdit(chip);
    });
  }

  // ---- Add ------------------------------------------------------------------

  add(event) {
    const isCommitKey = event.type === "keydown" && (event.key === "," || event.key === ";");
    if (event.type === "keydown" && event.key !== "Enter" && !isCommitKey) return;
    if (event.type === "keydown") event.preventDefault();

    const raw = (this.entryTarget.value || "").trim();
    if (!raw) return;
    if (this._existingValues().includes(raw)) {
      this.entryTarget.value = "";
      return;
    }

    const chip = this._buildChip(raw);
    this.chipsTarget.insertBefore(chip, this.entryTarget);
    this.entryTarget.value = "";

    // The user explicitly pressed Enter — commit to server.
    if (event.type === "keydown") this._submitForm();
  }

  // ---- Remove ---------------------------------------------------------------

  remove(event) {
    event.preventDefault();
    event.stopPropagation();
    const chip = event.currentTarget.closest(".jc-chip");
    if (!chip) return;
    chip.remove();
    this._submitForm();
  }

  // ---- Inline edit ----------------------------------------------------------

  edit(event) {
    // Ignore clicks on the × button (handled by `remove`).
    if (event.target.closest(".jc-chip__remove")) return;
    const chip = event.currentTarget.closest(".jc-chip");
    if (!chip || chip.classList.contains("jc-chip--editing")) return;
    this._enterEditMode(chip);
  }

  _enterEditMode(chip) {
    const labelEl = chip.querySelector(".jc-chip__label, span:not(.jc-chip__edit)");
    const hidden = chip.querySelector("input[type='hidden']");
    if (!labelEl || !hidden) return;

    const original = hidden.value;
    chip.classList.add("jc-chip--editing");

    const input = document.createElement("input");
    input.type = "text";
    input.value = original;
    input.className = "jc-chip__edit";
    // Best-effort sizing so the field roughly matches the chip text width.
    input.size = Math.max(original.length, 4);
    input.dataset.original = original;

    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); this._commitEdit(chip, input); }
      else if (e.key === "Escape") { e.preventDefault(); this._cancelEdit(chip, input); }
    });
    input.addEventListener("blur", () => this._commitEdit(chip, input));

    labelEl.replaceWith(input);
    input.focus();
    input.select();
  }

  _commitEdit(chip, input) {
    if (!chip.isConnected) return; // chip removed mid-edit
    const next = (input.value || "").trim();
    const prev = input.dataset.original;
    const hidden = chip.querySelector("input[type='hidden']");

    // Empty value → treat as deletion.
    if (!next) {
      chip.remove();
      this._submitForm();
      return;
    }

    // Duplicate of another chip → revert.
    const others = this._existingValues().filter((v) => v !== prev);
    if (others.includes(next)) {
      this._cancelEdit(chip, input);
      return;
    }

    const label = document.createElement("span");
    label.className = "jc-chip__label";
    label.textContent = next;
    input.replaceWith(label);
    if (hidden) hidden.value = next;
    chip.classList.remove("jc-chip--editing");

    if (next !== prev) this._submitForm();
  }

  _cancelEdit(chip, input) {
    const original = input.dataset.original;
    const label = document.createElement("span");
    label.className = "jc-chip__label";
    label.textContent = original;
    input.replaceWith(label);
    chip.classList.remove("jc-chip--editing");
  }

  // ---- Helpers --------------------------------------------------------------

  _bindChipForEdit(chip) {
    // Make sure the visible label is wrapped in a span we can replace later.
    if (!chip.querySelector(".jc-chip__label")) {
      const firstSpan = chip.querySelector("span:not(.jc-chip__remove)");
      if (firstSpan) firstSpan.classList.add("jc-chip__label");
    }
    chip.addEventListener("click", (e) => this.edit(e));
  }

  _existingValues() {
    return Array.from(this.chipsTarget.querySelectorAll("input[type='hidden']"))
      .map((input) => input.value);
  }

  _buildChip(value) {
    const wrapper = document.createElement("span");
    wrapper.className = "jc-chip";

    const label = document.createElement("span");
    label.className = "jc-chip__label";
    label.textContent = value;
    wrapper.appendChild(label);

    const hidden = document.createElement("input");
    hidden.type = "hidden";
    hidden.name = this.nameValue;
    hidden.value = value;
    wrapper.appendChild(hidden);

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "jc-chip__remove";
    btn.setAttribute("aria-label", `Retirer ${value}`);
    btn.dataset.action = "chip-input#remove";
    btn.innerHTML = '<i class="fa-solid fa-xmark" aria-hidden="true"></i>';
    wrapper.appendChild(btn);

    this._bindChipForEdit(wrapper);
    return wrapper;
  }

  _submitForm() {
    if (!this.autoSubmitValue) return;
    const form = this.element.closest("form");
    if (!form) return;
    // Native requestSubmit so Turbo / Rails handle the request lifecycle.
    if (typeof form.requestSubmit === "function") {
      form.requestSubmit();
    } else {
      form.submit();
    }
  }
}
